// src/components/Projects/ProjectView.jsx
import React, { useState, useEffect } from 'react';
import { projectsApi, orgsApi } from '../../services/api';
import { FolderKanban, Plus, Edit2, Trash2, X, Tag, AppWindow } from 'lucide-react';

const ENV_STYLES = {
  production:  { bg: 'rgba(239,68,68,.15)',  color: '#f87171',  label: 'Production' },
  staging:     { bg: 'rgba(245,158,11,.15)', color: '#fbbf24',  label: 'Staging' },
  development: { bg: 'rgba(59,130,246,.15)', color: '#60a5fa',  label: 'Development' },
  testing:     { bg: 'rgba(139,92,246,.15)', color: '#a78bfa',  label: 'Testing' },
};
const STATUS_STYLES = {
  active:   { color: '#22c55e', label: 'Active' },
  archived: { color: '#64748b', label: 'Archived' },
};
const COLORS = ['#3b82f6','#8b5cf6','#22c55e','#f59e0b','#ef4444','#06b6d4','#ec4899'];

const EMPTY_FORM = { name: '', description: '', orgId: '', environment: 'production', color: '#3b82f6', tags: '' };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-lg rounded-xl p-6"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 25px 50px rgba(0,0,0,.5)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} style={{ color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' };

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      {children}
    </div>
  );
}

export default function ProjectView() {
  const [projects, setProjects]   = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [envFilter, setEnvFilter] = useState('ALL');
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [deleteId, setDeleteId]   = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [projs, orgList] = await Promise.all([projectsApi.list(), orgsApi.list()]);
      setProjects(projs);
      setOrgs(orgList);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setForm({ ...EMPTY_FORM, orgId: orgFilter !== 'ALL' ? orgFilter : '' });
    setModal('create');
  }
  function openEdit(p) {
    setForm({ name: p.name, description: p.description || '', orgId: String(p.orgId || ''), environment: p.environment || 'production', color: p.color || '#3b82f6', tags: (p.tags || []).join(', ') });
    setModal(p);
  }

  async function save() {
    if (!form.name.trim() || !form.orgId) return;
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      if (modal === 'create') await projectsApi.create(payload);
      else await projectsApi.update(modal._id, payload);
      setModal(null);
      load();
    } finally { setSaving(false); }
  }

  async function confirmDelete(id) {
    await projectsApi.delete(id);
    setDeleteId(null);
    load();
  }

  const displayed = projects.filter(p => {
    if (orgFilter !== 'ALL' && String(p.orgId) !== orgFilter) return false;
    if (envFilter !== 'ALL' && p.environment !== envFilter) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <FolderKanban size={20} style={{ color: '#60a5fa' }} />
        <div>
          <h1 className="text-base font-semibold">Projects</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Organize your applications into projects within organizations</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
            style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
            <option value="ALL">All Organizations</option>
            {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
          </select>
          <select value={envFilter} onChange={e => setEnvFilter(e.target.value)}
            style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
            <option value="ALL">All Environments</option>
            {Object.entries(ENV_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
            <Plus size={14} /> New Project
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {displayed.map(p => {
              const org = orgs.find(o => String(o._id) === String(p.orgId));
              return <ProjectCard key={p._id} project={p} orgName={org?.name} onEdit={() => openEdit(p)} onDelete={() => setDeleteId(p._id)} />;
            })}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal === 'create' ? 'New Project' : 'Edit Project'} onClose={() => setModal(null)}>
          <Field label="Project Name *">
            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Core Platform" />
          </Field>
          <Field label="Organization *">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.orgId} onChange={e => setForm(f => ({ ...f, orgId: e.target.value }))}>
              <option value="">Select organization…</option>
              {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description…" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Environment">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}>
                {Object.entries(ENV_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Color">
              <div className="flex gap-2 pt-1">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                    style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: form.color === c ? '3px solid white' : '3px solid transparent', cursor: 'pointer', outline: 'none' }} />
                ))}
              </div>
            </Field>
          </div>
          <Field label="Tags (comma-separated)">
            <input style={inputStyle} value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. api, core, critical" />
          </Field>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setModal(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={save} disabled={saving || !form.name.trim() || !form.orgId}
              style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(59,130,246,.2)', border: '1px solid rgba(59,130,246,.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? .5 : 1 }}>
              {saving ? 'Saving…' : (modal === 'create' ? 'Create' : 'Save')}
            </button>
          </div>
        </Modal>
      )}

      {deleteId && (
        <Modal title="Delete Project?" onClose={() => setDeleteId(null)}>
          <p className="text-sm mb-5" style={{ color: 'var(--text-2)' }}>This will permanently delete the project. Applications in this project will need to be reassigned.</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleteId(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => confirmDelete(deleteId)} style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(239,68,68,.2)', border: '1px solid rgba(239,68,68,.4)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ProjectCard({ project, orgName, onEdit, onDelete }) {
  const env    = ENV_STYLES[project.environment] || ENV_STYLES.production;
  const status = STATUS_STYLES[project.status]    || STATUS_STYLES.active;
  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--bg-secondary)', border: `1px solid ${project.color || '#3b82f6'}30`, borderLeftWidth: 4 }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{project.name}</span>
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: env.bg, color: env.color }}>{env.label}</span>
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: `${status.color}18`, color: status.color }}>{status.label}</span>
          </div>
          {orgName && <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{orgName}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <IconBtn icon={Edit2} onClick={onEdit} />
          <IconBtn icon={Trash2} onClick={onDelete} danger />
        </div>
      </div>

      {project.description && (
        <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-2)' }}>{project.description}</p>
      )}

      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-3)' }}>
        <span className="flex items-center gap-1"><AppWindow size={11} />{project.appCount ?? 0} apps</span>
        {(project.tags || []).slice(0, 3).map(t => (
          <span key={t} className="flex items-center gap-1"
            style={{ padding: '1px 7px', borderRadius: 20, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
            <Tag size={9} />{t}
          </span>
        ))}
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, danger }) {
  return (
    <button onClick={onClick} style={{ padding: 5, borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: danger ? '#f87171' : 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}>
      <Icon size={13} />
    </button>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <FolderKanban size={40} style={{ color: 'var(--text-3)' }} />
      <div className="text-sm font-medium">No projects yet</div>
      <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-3)' }}>Create a project within an organization to group your applications.</p>
      <button onClick={onAdd}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
        style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
        <Plus size={14} /> New Project
      </button>
    </div>
  );
}
